'use client';

import Link from 'next/link';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useAuth, useT } from '@/hooks';
import { useDismissStreakBanner, useStreakStatus } from '@/services/query/streak.queries';

import { ROUTES, TOP_BANNER_HEIGHT } from '@/constants';

/**
 * Top-of-page streak promo banner — shown once per account until dismissed.
 * Sits directly below GeoBlockBanner (same sticky header region) so a
 * geo-blocked, logged-in user sees that message first; this is strictly
 * secondary and never overlaps or replaces it.
 *
 * Dismissal is permanent and account-level (persisted server-side via
 * UserStreak.bannerDismissedAt), not per-device/localStorage, so it stays
 * dismissed across browsers once closed. The header's StreakProgressPill
 * remains the ongoing entry point to /streaks after this is dismissed.
 */
export const StreakBanner = () => {
  const { t } = useT();
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useStreakStatus(isAuthenticated);
  const dismissBanner = useDismissStreakBanner();

  if (!isAuthenticated || isLoading || !data || data.bannerDismissed) return null;

  const progressPct = Math.max(0, Math.min(100, data.todaysProgressPct));

  return (
    <Alert
      icon={false}
      severity="warning"
      action={
        <IconButton
          size="small"
          aria-label={t('common.close', { defaultValue: 'Dismiss' })}
          onClick={() => dismissBanner.mutate()}
          sx={(theme) => ({ color: theme.palette.warning.main })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      sx={(theme) => ({
        borderRadius: 0,
        height: TOP_BANNER_HEIGHT,
        px: { xs: 2, sm: 4 },
        display: 'flex',
        alignItems: 'center',
        bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
        borderBottom: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.25 : 0.2)}`,
        backdropFilter: 'blur(8px)',
        '& .MuiAlert-message': {
          flex: 1,
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
        },
        '& .MuiAlert-action': {
          p: 0,
          mr: 0,
        },
      })}
    >
      <Link
        href={ROUTES.STREAKS}
        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}
      >
        <LocalFireDepartmentIcon
          fontSize="small"
          sx={(theme) => ({ color: theme.palette.warning.main, flexShrink: 0 })}
        />
        <Typography
          variant="body2"
          sx={(theme) => ({
            fontWeight: 700,
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            color: theme.palette.warning.main,
            whiteSpace: 'nowrap',
          })}
        >
          {t('streakBanner.keepGoing', { defaultValue: 'Keep your streak going —' })}
        </Typography>

        {/* Compact inline progress bar, same brand gradient as the header pill/streak dashboard */}
        <Box
          sx={{
            width: { xs: 56, sm: 84 },
            height: 6,
            borderRadius: 3,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progressPct}%`,
              borderRadius: 3,
              background: 'linear-gradient(90deg, #e8b046, #e0664f)',
              transition: 'width 0.6s ease',
            }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={(theme) => ({
            fontWeight: 700,
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            color: theme.palette.warning.main,
            whiteSpace: 'nowrap',
          })}
        >
          {progressPct >= 100
            ? t('streakBanner.targetHit', { defaultValue: "Today's target hit!" })
            : t('streakBanner.progress', {
                volume: data.todaysVolume.toFixed(0),
                target: data.todaysTarget.toFixed(0),
                defaultValue: `$${data.todaysVolume.toFixed(0)} of $${data.todaysTarget.toFixed(0)} today`,
              })}
        </Typography>
      </Link>
    </Alert>
  );
};

export default StreakBanner;
